import { __decorate } from "tslib";
import { noop } from 'lodash';
import { UI_EVENTS, VIDEO_EVENTS, EngineState as STATES, } from 'playable/dist/src/constants';
import playerAPI from 'playable/dist/src/core/player-api-decorator';
import { APPLICATION_CONFIG, WIDGET_CONFIG, CONTROL_DESCRIPTOR, } from './config';
import { addScript, addStyle } from './utils/load';
// This link hosts annoto bootstrap of version 1.6.18
// This bootstrap file is modified by Annoto team specifically for the Wix.
// For example, it uses custom domain (wix.annoto.io) for some requests like: auth.wix.annoto.io etc..
// The latest version of Annoto on today is 3.x.x. I tried to switch to it but the clientId we use doesn't work with the latest Annoto backend.
// We've contacted the Annoto team and they confirmed that they have to do something on their side in to let us use same clientId with the new version.
const ANNOTO_BOOTSTRAP_LINK = 'https://widget.wix.annoto.io/annoto-bootstrap.js';
const ANNOTO_ROOT_ID = 'annoto-app';
const ANNOTO_WRAPPER = 'playable-annoto-app-wrapper';
export class AnnotoModule {
    constructor(modules) {
        this.annotoWrapper = this.createAnnotoWrapper();
        this.onFullScreenCallback = noop;
        this.onControlsHideCallback = noop;
        this.onControlsShowCallback = noop;
        this.onTimeUpdateCallback = noop;
        this.onSeekCallback = noop;
        this.onReadyCallback = noop;
        this.onPauseCallback = noop;
        this.onPlayCallback = noop;
        this.onMediaChangeCallback = noop;
        this.handleFullscreenChange = () => {
            this.onFullScreenCallback();
        };
        this.handleControlsHide = () => {
            this.onControlsHideCallback();
        };
        this.handleControlsShow = () => {
            this.onControlsShowCallback();
        };
        this.handleTimeUpdate = () => {
            this.onTimeUpdateCallback();
        };
        this.handleSeekInProgress = () => {
            this.onSeekCallback();
        };
        this.handleStateChange = ({ nextState }) => {
            if (nextState === STATES.METADATA_LOADED) {
                this.onReadyCallback();
            }
            if (nextState === STATES.PAUSED) {
                this.onPauseCallback();
            }
            if (nextState === STATES.PLAYING) {
                this.onPlayCallback();
            }
            if (nextState === STATES.SRC_SET) {
                this.onMediaChangeCallback();
            }
        };
        const { fullScreenManager, rootContainer, engine, eventEmitter, mainUIBlock, } = modules;
        this.fullScreenManager = fullScreenManager;
        this.rootContainer = rootContainer;
        this.engine = engine;
        this.eventEmitter = eventEmitter;
        this.mainUIBlock = mainUIBlock;
        rootContainer.appendComponentElement(this.annotoWrapper);
        this.initEventListeners();
    }
    async getApi() {
        if (!window.AnnotoApiPromise) {
            throw new Error('AnnotoApiPromise is not defined');
        }
        return window.AnnotoApiPromise;
    }
    initEventListeners() {
        this.eventEmitter.on(VIDEO_EVENTS.STATE_CHANGED, this.handleStateChange);
        this.eventEmitter.on(VIDEO_EVENTS.SEEK_IN_PROGRESS, this.handleSeekInProgress);
        this.eventEmitter.on(VIDEO_EVENTS.CURRENT_TIME_UPDATED, this.handleTimeUpdate);
        this.eventEmitter.on(VIDEO_EVENTS.DURATION_UPDATED, this.handleTimeUpdate);
        this.eventEmitter.on(UI_EVENTS.MAIN_BLOCK_SHOW, this.handleControlsShow);
        this.eventEmitter.on(UI_EVENTS.MAIN_BLOCK_HIDE, this.handleControlsHide);
        this.eventEmitter.on(UI_EVENTS.FULL_SCREEN_STATE_CHANGED, this.handleFullscreenChange);
    }
    getAnnotoRoot() {
        const annotoRoot = document.getElementById(ANNOTO_ROOT_ID);
        if (annotoRoot) {
            return annotoRoot;
        }
        const newAnnotoRoot = document.createElement('div');
        newAnnotoRoot.setAttribute('id', ANNOTO_ROOT_ID);
        return newAnnotoRoot;
    }
    createAnnotoWrapper() {
        const annotoWrapper = document.createElement('div');
        annotoWrapper.setAttribute('data-hook', ANNOTO_WRAPPER);
        annotoWrapper.setAttribute('style', 'height: 100%;');
        return annotoWrapper;
    }
    async init(options, annoto) {
        const { locale, ssoAuthRequestHandle, mediaDetailsHandle, metadataChangeCallback = () => { }, uxEventCallback = () => { }, mediaId, } = options;
        const annotoRoot = this.getAnnotoRoot();
        this.annotoRoot = annotoRoot; // save for destroy
        this.annotoWrapper.appendChild(annotoRoot);
        const config = {
            ...APPLICATION_CONFIG,
            locale,
            ux: {
                ...APPLICATION_CONFIG.ux,
                ssoAuthRequestHandle,
            },
            widgets: [
                {
                    ...WIDGET_CONFIG,
                    player: {
                        ...WIDGET_CONFIG.player,
                        element: this.annotoWrapper,
                        api: this.getAnnotoPlayerAdapter({ mediaId }),
                        mediaDetails: mediaDetailsHandle,
                    },
                },
            ],
        };
        annoto.on('metadata', metadataChangeCallback);
        annoto.on('ux', uxEventCallback);
        return config;
    }
    async showAnnoto() {
        const annotoAPI = await this.getApi();
        await annotoAPI.show();
    }
    async hideAnnoto() {
        const annotoAPI = await this.getApi();
        await annotoAPI.hide();
    }
    async disableAnnoto() {
        const annotoAPI = await this.getApi();
        await annotoAPI.close();
    }
    async bootAnnoto(options) {
        if (window.AnnotoPromise) {
            // annoto.boot() should be called once for the page.
            // Once it is loaded you should use api.load(config)
            return;
        }
        window.AnnotoPromise = this.loadAnnoto();
        const annoto = await window.AnnotoPromise;
        const config = await this.init(options, annoto);
        window.AnnotoApiPromise = new Promise((resolve) => {
            annoto.on('ready', async (api) => {
                resolve(api);
            });
            annoto.boot(config);
        });
    }
    async loadAnnotoConfig(options) {
        const { AnnotoPromise, AnnotoApiPromise } = window;
        if (!AnnotoPromise || !AnnotoApiPromise) {
            throw new Error('Boot Annoto first');
        }
        const annoto = await AnnotoPromise;
        const config = await this.init(options, annoto);
        const api = await AnnotoApiPromise;
        // Before loading the new config closing Annoto if it's loaded
        // on some other video widget on the page.
        await api.close();
        await api.load(config);
    }
    async authenticate(ssoToken) {
        const api = await this.getApi();
        await api.auth(ssoToken);
    }
    async loadAnnoto() {
        return new Promise((resolve) => {
            addScript(ANNOTO_BOOTSTRAP_LINK, () => resolve(window.Annoto));
            // Dark theme
            addStyle('https://widget.wix.annoto.io/themes/wix/dist/annoto-theme.css?asvc');
        });
    }
    getAnnotoPlayerAdapter({ mediaId }) {
        const { fullScreenManager, engine, rootContainer, mainUIBlock } = this;
        return {
            init: () => true, // should return bool/promise
            isLive: () => {
                return (engine.attachedAdapter &&
                    engine.attachedAdapter.isDynamicContent &&
                    !engine.attachedAdapter.isDynamicContentEnded);
            },
            // Controls
            play: () => engine.play(),
            pause: () => engine.pause(),
            setCurrentTime: (time) => engine.seekTo(time),
            fullScreen: () => fullScreenManager.isInFullScreen,
            embeddableElement: () => rootContainer.getElement(),
            controlsHidden: () => !mainUIBlock._isContentShown,
            currentTime: () => engine.getCurrentTime(),
            duration: () => {
                if (engine.attachedAdapter) {
                    if (engine.attachedAdapter.isSeekAvailable) {
                        return engine.getDuration();
                    }
                    return 0;
                }
                return engine.getDuration();
            },
            paused: () => engine.isPaused,
            mediaSrc: () => mediaId,
            autoplay: () => engine.getAutoplay(),
            controlsDescriptor: () => CONTROL_DESCRIPTOR,
            fixFullScreen: () => false,
            // Events
            onReady: (cb) => {
                if (engine.isMetadataLoaded) {
                    cb();
                }
                this.onReadyCallback = cb;
            },
            onPlay: (cb) => {
                this.onPlayCallback = cb;
            },
            onPause: (cb) => {
                this.onPauseCallback = cb;
            },
            onSeek: (cb) => {
                this.onSeekCallback = cb;
            },
            onTimeUpdate: (cb) => {
                this.onTimeUpdateCallback = cb;
            },
            onMediaChange: (cb) => {
                this.onMediaChangeCallback = cb;
            },
            onControlsShow: (cb) => {
                this.onControlsShowCallback = cb;
            },
            onControlsHide: (cb) => {
                this.onControlsHideCallback = cb;
            },
            onFullScreen: (cb) => {
                this.onFullScreenCallback = cb;
            },
            remove: () => {
                this.onFullScreenCallback = noop;
                this.onControlsHideCallback = noop;
                this.onControlsShowCallback = noop;
                this.onTimeUpdateCallback = noop;
                this.onSeekCallback = noop;
                this.onReadyCallback = noop;
                this.onPauseCallback = noop;
                this.onPlayCallback = noop;
                this.onMediaChangeCallback = noop;
            },
        };
    }
    async destroy() {
        if (this.annotoRoot) {
            document.body.appendChild(this.annotoRoot);
        }
        const annotoAPI = await this.getApi();
        await annotoAPI.close();
        this.eventEmitter.off(VIDEO_EVENTS.STATE_CHANGED, this.handleStateChange, this);
        this.eventEmitter.off(VIDEO_EVENTS.SEEK_IN_PROGRESS, this.handleSeekInProgress, this);
        this.eventEmitter.off(VIDEO_EVENTS.CURRENT_TIME_UPDATED, this.handleTimeUpdate, this);
        this.eventEmitter.off(VIDEO_EVENTS.DURATION_UPDATED, this.handleTimeUpdate, this);
        this.eventEmitter.off(UI_EVENTS.MAIN_BLOCK_SHOW, this.handleControlsShow, this);
        this.eventEmitter.off(UI_EVENTS.MAIN_BLOCK_HIDE, this.handleControlsHide, this);
        this.eventEmitter.off(UI_EVENTS.FULL_SCREEN_STATE_CHANGED, this.handleFullscreenChange, this);
        this.engine = null;
    }
}
AnnotoModule.moduleName = 'annoto';
AnnotoModule.dependencies = [
    'eventEmitter',
    'engine',
    'rootContainer',
    'fullScreenManager',
    'mainUIBlock',
];
__decorate([
    playerAPI()
], AnnotoModule.prototype, "showAnnoto", null);
__decorate([
    playerAPI()
], AnnotoModule.prototype, "hideAnnoto", null);
__decorate([
    playerAPI()
], AnnotoModule.prototype, "disableAnnoto", null);
__decorate([
    playerAPI()
], AnnotoModule.prototype, "bootAnnoto", null);
__decorate([
    playerAPI()
], AnnotoModule.prototype, "loadAnnotoConfig", null);
__decorate([
    playerAPI()
], AnnotoModule.prototype, "authenticate", null);
//# sourceMappingURL=module.js.map